"use client";

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Avatar, Typography, Box, Chip, Link, Stack, Divider, CardContent } from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import BugReportIcon from '@mui/icons-material/BugReport';


// 生年月日から年齢を計算するヘルパー関数
const calculateAge = (birthDateString) => {
  if (!birthDateString) return null;
  try {
    const birthDate = new Date(birthDateString);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  } catch (e) {
    console.error("Invalid birth date", birthDateString);
    return null;
  }
};

export default function TeamMemberCard({ memberInfo }) {
  const { t } = useTranslation('adminTeam');
  const researcher = memberInfo.researcher;
  const age = researcher ? calculateAge(researcher.birth_date) : null;

  // 研究者に関連する統計数を取得
  const pubCount = researcher?.researcher_stats?.[0]?.publication_count ?? 0;
  const taxaCount = researcher?.researcher_stats?.[0]?.taxa_count ?? 0;

  let externalLinks = null;
  if (researcher && researcher.links) {
    try {
      // DBから取得したlinksが文字列かオブジェクトか分からないため、両対応
      const parsedLinks = typeof researcher.links === 'string'
        ? JSON.parse(researcher.links)
        : researcher.links;

      if (parsedLinks && typeof parsedLinks === 'object') {
        externalLinks = Object.entries(parsedLinks);
      }
    } catch (e) {
      console.error("Failed to parse researcher links:", e);
    }
  }

  return (
    <Card sx={{ width: { xs: '100%', sm: '90%', md: 800 }, p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Avatar
        alt={memberInfo.name}
        src={memberInfo.avatarUrl}
        sx={{ width: 160, height: 160, mb: 3, border: '2px solid', borderColor: 'divider' }}
      />
      <CardContent sx={{ width: '100%', textAlign: 'center', p: 0 }}>
        <Stack spacing={2}>
          {/* Top section */}
          <Box>
            <Typography variant="h4" component="div" fontWeight="bold">
              {memberInfo.name} {age && `(${age})`}
            </Typography>
            <Typography variant="h6" color="primary.main" sx={{ mb: 1 }}>
              {memberInfo.role}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {memberInfo.affiliation}
            </Typography>
          </Box>

          <Divider />

          {/* Description */}
          <Typography variant="body1">
            {memberInfo.description}
          </Typography>

          {/* Stats */}
          {researcher && (
            <Box>
              <Stack direction="row" spacing={3} alignItems="center" justifyContent="center">
                <Box>
                  <Typography variant="h5" fontWeight="medium">{pubCount.toLocaleString()}</Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5} color="text.secondary" justifyContent="center">
                    <MenuBookIcon fontSize="small" />
                    <Typography variant="caption">{t('publications', 'Publications')}</Typography>
                  </Stack>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box>
                  <Typography variant="h5" fontWeight="medium">{taxaCount.toLocaleString()}</Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5} color="text.secondary" justifyContent="center">
                    <BugReportIcon fontSize="small" />
                    <Typography variant="caption">{t('taxaDescribed', 'Taxa Described')}</Typography>
                  </Stack>
                </Box>
              </Stack>
            </Box>
          )}

          {/* Links */}
          {externalLinks && externalLinks.length > 0 && (
            <Box sx={{ pt: 1 }}>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="center">
                {externalLinks.map(([name, url]) => (
                  <Chip key={name} icon={<LinkIcon />} label={name} component="a" href={url} target="_blank" rel="noopener noreferrer" clickable variant="outlined" size="small" />
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
